'use client';

import { useApprovedSponsorsForEvent } from '@/tenants/_shared/hooks/use-public-sponsors';

interface SponsorLogosSectionProps {
  eventId: string | undefined;
  title?: string;
  variant?: 'light' | 'dark';
  bordered?: boolean;
}

const levelSize: Record<string, { img: string; px: number }> = {
  premium: { img: 'w-[220px] h-[220px]', px: 220 },
  major: { img: 'w-[200px] h-[200px]', px: 200 },
  minor: { img: 'w-[100px] h-[100px]', px: 100 },
};

export function SponsorLogosSection({
  eventId,
  title = 'Event Sponsors',
  variant = 'light',
  bordered = true,
}: SponsorLogosSectionProps) {
  const { data } = useApprovedSponsorsForEvent(eventId);

  if (!data?.sponsors?.length) return null;

  const cardClass = bordered
    ? variant === 'dark'
      ? 'bg-stone-900 rounded-xl border border-stone-800 p-6'
      : 'bg-white rounded-xl border border-stone-200 p-6'
    : '';

  return (
    <div className={cardClass}>
      <h2
        className={`font-bold mb-4 ${bordered ? 'text-xl mb-6' : 'text-lg'} ${variant === 'dark' ? 'text-white' : 'text-stone-900'}`}
      >
        {title}
      </h2>
      <div className="flex flex-col gap-6">
        {data.sponsors.map((group) => {
          if (!group.sponsors.length) return null;
          const size = levelSize[group.level] || levelSize.minor;

          return (
            <div key={group.level} className="flex flex-wrap gap-4">
              {group.sponsors.map((sponsor) => {
                const content = sponsor.logoUrl ? (
                  <img
                    src={sponsor.logoUrl}
                    alt={sponsor.name}
                    width={size.px}
                    height={size.px}
                    className={`${size.img} object-contain rounded-xl border bg-white border-stone-300`}
                  />
                ) : null;

                return sponsor.websiteUrl ? (
                  <a
                    key={sponsor.sponsorId}
                    href={sponsor.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opacity-80 hover:opacity-100 transition-opacity"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={sponsor.sponsorId}>{content}</div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
